import React, { useState, useEffect } from 'react';
import { Printer, Footprints, Truck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ExportCsvButton } from './ExportCsvButton';
import api from '../../lib/api';

export const ParadeRosterAdmin = () => {
  const [signups, setSignups] = useState([]);

  useEffect(() => {
    api.get('/admin/parade-roster').then((res) => setSignups(res.data)).catch(() => {});
  }, []);

  const walkers = signups.filter((s) => s.role === 'walker');
  const floats = signups.filter((s) => s.role === 'float');

  const handlePrint = () => {
    const section = (title, list) => `
      <h2>${title} (${list.length})</h2>
      <table><tr><th>#</th><th>Name</th><th>Email</th><th>Note</th><th>Check-In</th></tr>
      ${list.map((s, i) => `<tr><td>${i + 1}</td><td>${s.name}</td><td>${s.email || ''}</td><td>${s.note || ''}</td><td class="box"></td></tr>`).join('')}
      </table>`;
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>WCLC Parade Roster</title><style>
      body{font-family:Georgia,serif;color:#3c4a2a;padding:24px}
      h1{font-size:22px;margin-bottom:2px} p.sub{margin-top:0;color:#777}
      h2{font-size:16px;margin:18px 0 6px;color:#a3688f}
      table{width:100%;border-collapse:collapse;font-size:13px}
      th,td{border:1px solid #bbb;padding:6px 8px;text-align:left}
      th{background:#f1e3eb}
      td.box{width:70px}
    </style></head><body>
      <h1>Lincolnton Christmas Parade — WCLC Lineup Roster</h1>
      <p class="sub">Sunday, Nov 29, 2026 • 4:00–5:00 PM • Historic Downtown Lincolnton</p>
      ${section('Parade Walkers', walkers)}
      ${section('Float Helpers', floats)}
    </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <Card className="bg-white border border-[#bd8cae]/30 shadow-sm mb-6" data-testid="parade-roster-admin">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <div>
          <CardTitle className="font-serif text-xl text-[#3c4a2a]">Christmas Parade Roster</CardTitle>
          <p className="text-sm text-[#3c4a2a]/60 mt-1">{walkers.length} walkers • {floats.length} float helpers</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handlePrint}
            size="sm"
            variant="outline"
            disabled={!signups.length}
            data-testid="parade-roster-print-btn"
            className="border-[#a3688f] text-[#a3688f] hover:bg-[#a3688f] hover:text-white"
          >
            <Printer className="w-4 h-4 mr-2" /> Print Lineup List
          </Button>
          <ExportCsvButton
            endpoint="/admin/parade-roster"
            filename="wclc-parade-roster.csv"
            label="Download CSV"
            testId="parade-roster-csv-btn"
            columns={[
              { key: 'name', label: 'Name' },
              { key: 'email', label: 'Email' },
              { key: 'role', label: 'Role', value: (r) => (r.role === 'walker' ? 'Walker' : 'Float Helper') },
              { key: 'note', label: 'Note' },
            ]}
          />
        </div>
      </CardHeader>
      <CardContent>
        {!signups.length ? (
          <p className="text-sm text-[#3c4a2a]/50" data-testid="parade-roster-empty">No parade signups yet — members sign up from their dashboard.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {[['Walkers', walkers, Footprints], ['Float Helpers', floats, Truck]].map(([label, list, Icon]) => (
              <div key={label}>
                <p className="text-xs uppercase tracking-wider text-[#a3688f] font-semibold mb-2 flex items-center gap-1">
                  <Icon className="w-3.5 h-3.5" /> {label} ({list.length})
                </p>
                <div className="space-y-1.5">
                  {list.map((s, i) => (
                    <div key={`${label}-${i}`} className="bg-[#f1e3eb]/50 rounded px-3 py-1.5 text-sm text-[#3c4a2a]">
                      <span className="font-medium">{s.name}</span>
                      <span className="text-[#3c4a2a]/55 ml-2 text-xs">{s.email}</span>
                      {s.note && <p className="text-xs text-[#3c4a2a]/55">{s.note}</p>}
                    </div>
                  ))}
                  {!list.length && <p className="text-xs text-[#3c4a2a]/45">None yet.</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
